import React, { ReactElement, useMemo } from 'react'
import { Controller, SubmitHandler } from 'react-hook-form'

import BootstrapModalLayout from '../../ui/BootstrapModalLayout'
import isAura from '../../lib/isAura'
import { ChargePoint, useChargePointUpdateMutation } from '../../common/redux/api/web-api'
import { isChargingConnector } from '../../lib/findChargingConnector'
import Input from '../../common/components/Input'
import { SFormRow, SLabelText } from '../../common/styles/Form'
import { InfoIcon } from '../../lib/icons/info-icon'
import Text from '../../ui/Text'
import { ICapiFormInput } from '../../common/hooks/useCapiFormValidation'
import { mapCapiChargepoint, mapOcppChargepoint } from '../../common/utils/objectMappers'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import { IOcppFormInput } from '../../common/hooks/useOcppFormValidation'
import { Combobox } from '../../common/components/combobox'
import { transformLBCurrentInput } from '../../common/features/loadbalancer-group/loadbalancer-group-modal'
import useKeyboardHandle from '../../common/hooks/useKeyboardHandle'
import getInstallationPhaseMode from '../../lib/charge-point/getInstallationPhaseMode'
import RowDivider from '../../common/components/RowDivider'
import useChargePointFormValidation from '../../common/hooks/useChargePointFormValidation'
import { getChargerPhaseList } from '../../common/utils/chargePointUtils'
import useText from '../../common/hooks/useText'

type EditChargePointSettingsType = {
    chargePoint: ChargePoint
    closeModal: () => void
}

export default ({ chargePoint, closeModal }: EditChargePointSettingsType): ReactElement => {
    const text = useText()

    const installationPhaseMode = useMemo(() => getInstallationPhaseMode(chargePoint), [chargePoint])

    const phases = getChargerPhaseList(chargePoint?.productId, chargePoint?.earthingSystem, installationPhaseMode)
    const phaseOptions = phases.map((phase: string) => ({ value: phase, label: phase }))

    const chargerFormValidation = useChargePointFormValidation(chargePoint)

    const {
        handleSubmit,
        control,
        formState: { errors, isDirty, isValid },
    } = chargerFormValidation

    const [updateChargePoint, { isLoading: chargePointUpdating }] = useChargePointUpdateMutation()
    const saveChargePoint: SubmitHandler<ICapiFormInput | IOcppFormInput> = async (formData) => {
        try {
            await updateChargePoint({
                chargePoint: chargePoint?.isOcpp
                    ? mapOcppChargepoint(chargePoint, formData)
                    : mapCapiChargepoint(chargePoint, formData),
            }).unwrap()
            notifySuccess(text('msgChargerUpdated'))
        } catch (error) {
            notifyError(text('msgChargerFailedToUpdate'))
        } finally {
            closeModal()
        }
    }

    useKeyboardHandle({
        onEnterPress: () => {
            if (isValid) {
                handleSubmit(saveChargePoint)()
            }
        },
        onEscapePress: closeModal,
    })

    return (
        <BootstrapModalLayout
            size={'md'}
            title={text('lblLoadBalancerSettingsChargePoint')}
            actions={[
                {
                    label: text('btnCancel'),
                    callback: () => closeModal(),
                    btn: 'btn-secondary',
                },
                {
                    label: text('btnSave'),
                    callback: handleSubmit(saveChargePoint),
                    type: 'submit',
                    form: 'add-form',
                    disabled: !isDirty || !isValid || chargePointUpdating,
                    loading: chargePointUpdating,
                },
            ]}
        >
            <div>
                <span className="flex flex-wrap justify-content-between w-6" key={chargePoint?.loadBalancerGroupId}>
                    <div>
                        <SLabelText data-testid={`charger-id-${chargePoint?.id}`}>{text('lblId')}</SLabelText>
                        {chargePoint?.id}
                    </div>
                    <div>
                        <SLabelText data-testid={`charger-name-${chargePoint?.name}`}>{text('lblName')}</SLabelText>
                        {chargePoint?.name}
                    </div>
                </span>
                <RowDivider />

                {chargePoint?.loadBalancerGroupId &&
                    chargePoint?.connectors
                        ?.filter((connector) => isChargingConnector(connector))
                        ?.map((connector) => (
                            <span key={connector?.connectorId}>
                                {isAura(chargePoint) && (
                                    <div className=" mt-4">
                                        {connector?.connectorId === 1 ? (
                                            text('lblLeftSocket')
                                        ) : (
                                            <>
                                                <RowDivider />
                                                {text('lblRightSocket')}
                                            </>
                                        )}
                                    </div>
                                )}
                                <div key={connector?.connectorId} className="grid mt-4">
                                    <div className="col-12 lg:col-6">
                                        <SFormRow>
                                            <div
                                                className="flex gap-2 mb-2"
                                                data-testid={'lbOfflineCurrent-field-' + connector.connectorId}
                                            >
                                                <Text code="lblLoadBalancerOfflineCurrentLimitField" />

                                                <InfoIcon
                                                    title={text('msgLoadBalancerOfflineCurrentLimitField')}
                                                    size={18}
                                                />
                                            </div>

                                            <Controller
                                                name={`lbc_${connector?.connectorId}`}
                                                control={control}
                                                render={({ field: { onChange, value } }) => (
                                                    <Input
                                                        type="number"
                                                        required
                                                        id={`lbc_${connector?.connectorId}`}
                                                        data-testid={'lbOfflineCurrent-input-' + connector?.connectorId}
                                                        onChange={(e) => {
                                                            const transformedValue = parseInt(e.target.value)
                                                            onChange(transformLBCurrentInput.input(transformedValue))
                                                        }}
                                                        error={errors[`lbc_${connector?.connectorId}`]?.message}
                                                        value={value}
                                                    />
                                                )}
                                            />
                                        </SFormRow>
                                    </div>
                                    <div className="col-12 lg:col-6">
                                        <SFormRow>
                                            <div
                                                className="flex gap-2 mb-2"
                                                data-testid={'lbOfflinePhase-field-' + connector?.connectorId}
                                            >
                                                <Text code="lblLoadBalancerOfflinePhase" />

                                                <InfoIcon title={text('msgLoadBalancerOfflinePhase')} size={18} />
                                            </div>

                                            <div
                                                className="mb-6"
                                                data-testid={'lbOfflinePhase-field-' + connector?.connectorId}
                                            >
                                                <Controller
                                                    name={`lbOfflinePhase_${connector?.connectorId}`}
                                                    control={control}
                                                    render={({ field: { value, onChange } }) => (
                                                        <Combobox
                                                            enableDefaultOption={false}
                                                            data-testid={`installationPhase_${connector.connectorId}`}
                                                            id={`installationPhase_${connector.connectorId}`}
                                                            options={phaseOptions}
                                                            value={value}
                                                            onChange={(option) => {
                                                                onChange(option.value)
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </SFormRow>
                                    </div>
                                </div>
                            </span>
                        ))}
            </div>
        </BootstrapModalLayout>
    )
}
